// ════════════════════════════════════════════════════════════
// useVisibleNavItems — nav filtrée par rôle (Sprint Shell)
// ════════════════════════════════════════════════════════════
// Dérive la nav visible depuis NAV_ITEMS + canAccessPath (source
// de vérité unique). Partagé par Sidebar / SidebarDrawer /
// MobileBottomNav / CommandPalette.
// ════════════════════════════════════════════════════════════
import { useMemo } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { canAccessPath, type AppRole } from '@/types/auth';
import { NAV_ITEMS, NAV_GROUP_ORDER, type NavGroup, type NavItem } from '@/config/navigation';

export interface NavGroupBucket {
  group: NavGroup;
  items: NavItem[];
}

export interface VisibleNav {
  role: AppRole | null;
  /** Tous les items autorisés (ordre source). */
  visible: NavItem[];
  /** Items « haut » sans groupe (Accueil, Dashboard). */
  top: NavItem[];
  /** Items groupés, groupes vides exclus, dans NAV_GROUP_ORDER. */
  groups: NavGroupBucket[];
  /** Items épinglés en bas (Assistant IA, Mon compte). */
  footer: NavItem[];
  /** Items exposés dans la bottom nav mobile. */
  bottomNav: NavItem[];
}

export function useVisibleNavItems(): VisibleNav {
  const { profile } = useAuth();
  const role = profile?.role ?? null;

  return useMemo<VisibleNav>(() => {
    const visible = NAV_ITEMS.filter((item) => canAccessPath(role, item.to));
    const top = visible.filter((item) => !item.group && !item.footer);
    const footer = visible.filter((item) => item.footer);
    const groups = NAV_GROUP_ORDER.map<NavGroupBucket>((group) => ({
      group,
      items: visible.filter((item) => item.group === group),
    })).filter((bucket) => bucket.items.length > 0);
    const bottomNav = visible.filter((item) => item.bottomNav);

    return { role, visible, top, groups, footer, bottomNav };
  }, [role]);
}
